import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  InputNumber,
  Typography,
  Row,
  Col,
  Spin,
  Empty,
  Input,
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  GiftOutlined,
  NumberOutlined,
  CodeOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  useGetReferralCodesQuery,
  useCreateReferralCodeMutation,
  useDeleteReferralCodeMutation,
} from "../redux/rtk/apiRtk";

const { Title, Text } = Typography;

export default function Referalcode() {
  // Theme color
  const primaryColor = "#A40165";

  // State
  const [attempts, setAttempts] = useState(20);
  const [manualCode, setManualCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [animateCards, setAnimateCards] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // RTK Query hooks
  const { data: referalCode, isLoading: loading } = useGetReferralCodesQuery();
  const [createReferralCode, { isLoading: createLoading }] =
    useCreateReferralCodeMutation();
  const [deleteReferralCode, { isLoading: deleteLoading }] =
    useDeleteReferralCodeMutation();

  // Animation effect
  useEffect(() => {
    setTimeout(() => {
      setAnimateCards(true);
    }, 100);
  }, []);

  // Handle attempts change
  const handleAttemptsChange = (value) => {
    setAttempts(value);
  };

  // Handle manual code change with validation
  const handleManualCodeChange = (e) => {
    const value = e.target.value;
    setManualCode(value);

    // Validate if user has entered something
    if (value) {
      if (!/^[A-Za-z0-9]{4,10}$/.test(value)) {
        setCodeError("Code must be 4-10 alphanumeric characters");
      } else {
        setCodeError("");
      }
    } else {
      setCodeError("");
    }
  };

  // Create referral code
  const handleCreateCode = async () => {
    if (!attempts || attempts < 1) {
      toast.error("Please enter a valid number of attempts");
      return;
    }

    // Validate manual code if provided
    if (manualCode && !/^[A-Za-z0-9]{4,10}$/.test(manualCode)) {
      toast.error(
        "Invalid referral code format. Must be 4-10 alphanumeric characters."
      );
      return;
    }

    try {
      const payload = { atemps: attempts };
      if (manualCode) {
        payload.code = manualCode;
      }

      await createReferralCode(payload).unwrap();
      toast.success("Referral code created successfully");
      setManualCode("");
      setCodeError("");
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to create referral code";
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Delete referral code
  const handleDeleteCode = async (id) => {
    try {
      setDeleteId(id);
      await deleteReferralCode(id).unwrap();
      toast.success("Referral code deleted successfully");
    } catch (error) {
      toast.error("Failed to delete referral code");
      console.error(error);
    } finally {
      setDeleteId(null);
    }
  };

  // Copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Referral code copied to clipboard");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-4 sm:p-6 lg:p-8"
    >
      {/* Header Card */}
      <Card
        className="mb-6 rounded-xl shadow-md overflow-hidden"
        bodyStyle={{ padding: "24px" }}
        style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.07)" }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={12}>
            <div className="flex items-center">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-full mr-4"
                style={{ background: `${primaryColor}15` }}
              >
                <GiftOutlined style={{ fontSize: 22, color: primaryColor }} />
              </div>
              <div>
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    marginBottom: "4px",
                    color: primaryColor,
                  }}
                >
                  Referral Code Management
                </Title>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Create and manage referral codes for your users
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-100 flex-1 sm:flex-initial">
                  <NumberOutlined style={{ color: primaryColor }} />
                  <InputNumber
                    min={1}
                    max={2000}
                    value={attempts}
                    onChange={handleAttemptsChange}
                    placeholder="Attempts"
                    style={{
                      width: "100px",
                      border: "none",
                      background: "transparent",
                    }}
                    controls={false}
                  />
                  <Text type="secondary">attempts*</Text>
                </div>

                <div
                  className="flex-1 sm:flex-initial "
                  style={{ minWidth: "200px" }}
                >
                  <Input
                    prefix={<CodeOutlined style={{ color: primaryColor }} />}
                    placeholder="Custom code (optional)"
                    value={manualCode}
                    onChange={handleManualCodeChange}
                    maxLength={10}
                    status={codeError ? "error" : ""}
                    // style={{ height: "40px" }}
                  />
                  {codeError && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      {codeError}
                    </Text>
                  )}
                </div>

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateCode}
                  loading={createLoading}
                  disabled={!!codeError}
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    height: "40px",
                    minWidth: "120px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Create Code
                </Button>
              </div>
              <Text
                type="secondary"
                style={{ fontSize: "12px", marginTop: "-8px" }}
              >
                Leave code empty for auto-generation. Custom codes must be 4-10
                alphanumeric characters.
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Referral Codes List */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" tip="Loading referral codes..." />
        </div>
      ) : (
        <div className="mt-6">
          {referalCode && referalCode.length > 0 ? (
            <Row gutter={[16, 16]}>
              {referalCode.map((item, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={item._id}>
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate={animateCards ? "visible" : "hidden"}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      className="rounded-xl overflow-hidden h-full"
                      style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div
                          className="flex items-center justify-center w-8 h-8 rounded-full"
                          style={{ background: `${primaryColor}15` }}
                        >
                          <GiftOutlined
                            style={{ fontSize: 16, color: primaryColor }}
                          />
                        </div>
                        <Tag
                          color={primaryColor}
                          style={{
                            borderRadius: "12px",
                            fontWeight: 500,
                            opacity: 0.9,
                          }}
                        >
                          {item.atemps} Attempts
                        </Tag>
                      </div>

                      <div className="mb-2">
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Referral Code
                        </Text>
                        <div className="mt-1 p-2 bg-gray-50 border border-gray-100 rounded-md flex items-center justify-between">
                          <Text
                            strong
                            style={{ fontSize: "16px", letterSpacing: "0.5px" }}
                          >
                            {item.code}
                          </Text>
                          <div className="flex">
                            <Button
                              type="text"
                              size="small"
                              icon={
                                <CopyOutlined style={{ color: primaryColor }} />
                              }
                              onClick={() => copyToClipboard(item.code)}
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={
                                deleteLoading && deleteId === item._id ? (
                                  <Spin size="small" />
                                ) : (
                                  <DeleteOutlined
                                    style={{ color: "#ff4d4f" }}
                                  />
                                )
                              }
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to delete this code?"
                                  )
                                ) {
                                  handleDeleteCode(item._id);
                                }
                              }}
                              disabled={deleteLoading && deleteId === item._id}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Created:{" "}
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Text>
                      </div>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">
                  No referral codes found. Create one to get started.
                </Text>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateCode}
                style={{
                  backgroundColor: primaryColor,
                  borderColor: primaryColor,
                }}
              >
                Create Referral Code
              </Button>
            </Empty>
          )}
        </div>
      )}

      <ToastContainer />

      {/* Custom styles */}
      <style jsx="true">{`
        .ant-input-number-input {
          height: 24px;
          font-size: 14px;
        }

        .ant-card-actions {
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }

        .ant-card-actions > li {
          margin: 6px 0;
        }

        .ant-empty {
          margin: 32px 0;
        }
      `}</style>
    </motion.div>
  );
}

// Custom Tag component
const Tag = ({ children, color, style }) => {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        fontSize: "12px",
        lineHeight: "20px",
        color: color,
        backgroundColor: `${color}15`,
        border: `1px solid ${color}30`,
        borderRadius: "4px",
        ...style,
      }}
    >
      {children}
    </span>
  );
};
